import { useVoix } from "./voix.js";
import { useAccessoire } from "./profil.js";

/**
 * Hoa, le moaï. Dessiné en cinq tons (`--m1` le plus clair à `--m5` le plus
 * sombre) que chaque thème redéfinit : gris pour « sombre » et « clair »,
 * or pour « classique ».
 */
/**
 * Les expressions de Hoa (planche « Hoa_illustration », découpée en
 * pastilles rondes) :
 *   neutre   — par défaut ;
 *   sourire  — ça avance (une proposition, le chat) ;
 *   pouce    — une validation (rendez-vous, compte, c'est noté) ;
 *   surpris  — quelque chose arrive (une arrivée, une annonce, le temps) ;
 *   question — une question, un doute, un souci ;
 *   mefiant  — un refus, une sanction ;
 *   clin     — un clin d'œil (boutique, astuce) ;
 *   rire     — une belle fin de soirée ;
 *   joie     — la tribu est réunie, Hoa est libéré.
 */
export type Expression = "neutre" | "sourire" | "rire" | "mefiant" | "surpris" | "clin" | "pouce" | "question" | "joie";

export function Moai(props: { libre?: boolean; toujours?: boolean; accessoire?: string | null; expression?: Expression; dessin?: boolean }): JSX.Element {
  // Dans un événement, l'organisateur prend la parole — et l'image.
  const voix = useVoix();
  if (voix && !props.toujours && (voix.image || voix.nom)) {
    return (
      <span className="hoa-moai hoa-voix" aria-hidden="true">
        {voix.image ? <img src={voix.image} alt="" /> : <b>{voix.nom.trim()[0]?.toUpperCase()}</b>}
      </span>
    );
  }
  if (!props.dessin) {
    const e: Expression = props.expression ?? (props.libre ? "joie" : "neutre");
    return (
      <span className="hoa-moai hoa-illu" aria-hidden="true">
        <img src={`/hoa/hoa-${e}.webp`} alt="" draggable={false} />
      </span>
    );
  }
  return <MoaiDessin libre={props.libre} accessoire={props.accessoire} />;
}

/** L'ancien moaï dessiné (avec ses accessoires), gardé pour quand ils seront redessinés. */
function MoaiDessin(props: { libre?: boolean; accessoire?: string | null }): JSX.Element {
  const porte = useAccessoire();
  const accessoire = props.accessoire !== undefined ? props.accessoire : porte;
  return (
    <span className="hoa-moai" aria-hidden="true">
      <svg viewBox="0 0 264 564" focusable="false">
        <g transform="translate(12,12)">
          <path fill="var(--m2)" d="M104 348C66 352 34 368 18 394 9 408 6 420 6 436L6 508C6 526 16 540 34 540L206 540C224 540 234 526 234 508L234 436C234 418 230 406 220 392 204 368 176 352 196 348Z" />
          <path fill="var(--m3)" d="M152 350C160 400 162 470 158 540L206 540C224 540 234 526 234 508L234 436C234 418 230 406 220 392 204 368 176 352 196 348Z" />
          <path fill="var(--m2)" d="M96 2C62 8 36 26 28 48 24 62 22 80 21 104 16 110 12 118 10 132 11 160 14 190 20 220 22 238 32 250 46 258L50 318C56 338 76 350 100 352L200 352C218 344 226 326 226 300L226 186C226 140 224 96 216 60 208 30 186 10 146 2Z" />
          <path fill="var(--m3)" d="M146 2C170 48 180 112 182 182 184 256 178 314 168 352L200 352C218 344 226 326 226 300L226 186C226 140 224 96 216 60 208 30 186 10 146 2Z" />
          <path fill="var(--m4)" d="M190 26C204 74 212 130 212 192 212 264 206 316 196 352L200 352C218 344 226 326 226 300L226 186C226 140 224 96 216 60 212 46 202 34 190 26Z" />
          <path fill="var(--m1)" d="M96 2C62 8 36 26 28 48 25 60 23 74 22 92L176 66C172 46 164 26 150 4 132 1 112 0 96 2Z" />
          <path fill="var(--m4)" d="M22 92L176 66C182 76 186 86 189 98L172 100C169 90 164 82 157 76L24 100Z" />
          {props.libre ? (
            <>
              <path fill="var(--m5)" d="M34 112C52 110 70 110 88 112L88 118C70 118 52 118 36 118Z" />
              <path fill="var(--m5)" d="M156 90C170 88 184 88 196 90L196 96C184 96 170 96 158 96Z" />
            </>
          ) : (
            <>
              <path fill="var(--m5)" d="M34 104C52 100 70 98 88 100 90 116 90 130 88 142 68 142 50 134 38 124Z" />
              <path fill="var(--m5)" d="M156 78C170 74 184 74 194 78 197 92 198 104 197 116 184 116 170 110 161 102Z" />
            </>
          )}
          <path fill="var(--m1)" d="M98 102L124 94 150 202 94 213Z" />
          <path fill="var(--m4)" d="M124 94L150 202 168 194 148 100Z" />
          <path fill="var(--m5)" d="M74 268C104 258 140 252 172 250L174 266C142 270 106 278 76 286Z" />
          <path fill="var(--m2)" d="M33 110C22 114 16 126 17 142L22 214C24 234 33 246 46 248L47 232C38 229 33 221 32 208L28 144C27 133 31 126 38 122Z" />
          {accessoire ? <Accessoire id={accessoire} /> : null}
        </g>
      </svg>
    </span>
  );
}

/** Ce que porte le moaï (boutique, rayon « HOA »). */
function Accessoire(props: { id: string }): JSX.Element | null {
  switch (props.id) {
    case "hoa-lunettes":
      return (
        <g>
          <rect x="26" y="92" width="70" height="56" rx="16" fill="var(--m5)" fillOpacity="0.45" stroke="var(--m5)" strokeWidth="10" />
          <rect x="148" y="68" width="56" height="52" rx="14" fill="var(--m5)" fillOpacity="0.45" stroke="var(--m5)" strokeWidth="10" />
          <path d="M96 112L148 96" stroke="var(--m5)" strokeWidth="10" strokeLinecap="round" />
        </g>
      );
    case "hoa-mariniere":
      return (
        <g>
          <clipPath id="hoa-corps">
            <path d="M104 348C66 352 34 368 18 394 9 408 6 420 6 436L6 508C6 526 16 540 34 540L206 540C224 540 234 526 234 508L234 436C234 418 230 406 220 392 204 368 176 352 196 348Z" />
          </clipPath>
          <g clipPath="url(#hoa-corps)" fill="var(--m5)" fillOpacity="0.85">
            <rect x="0" y="384" width="240" height="18" />
            <rect x="0" y="424" width="240" height="18" />
            <rect x="0" y="464" width="240" height="18" />
            <rect x="0" y="504" width="240" height="18" />
          </g>
        </g>
      );
    case "hoa-lierre":
      return (
        <g fill="var(--m4)" stroke="var(--m5)" strokeWidth="5">
          {[
            [40, 44, -30],
            [70, 24, -15],
            [104, 12, 0],
            [140, 10, 10],
            [174, 22, 25],
            [202, 44, 40],
          ].map(([x, y, r]) => (
            <ellipse key={x} cx={x} cy={y} rx="30" ry="17" transform={`rotate(${r} ${x} ${y})`} />
          ))}
        </g>
      );
    default:
      return null;
  }
}
